import { observer } from "mobx-react-lite";
import { PlusIcon } from "lucide-react";

import { useParameters } from "../store";

import { DecisionQuestionCard } from "./DecisionQuestionCard";
import { createQuestion } from "./utils";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DecisionQuestion } from "@/types/prompt-blueprint";

const MAX_QUESTIONS = 400;

export const DecisionQuestionsEditor = observer(() => {
  const store = useParameters();
  const questions = store.decisionQuestions;
  const nameCounts = questions.reduce<Record<string, number>>(
    (counts, question) => ({ ...counts, [question.name]: (counts[question.name] ?? 0) + 1 }),
    {},
  );

  const updateQuestion = (index: number, question: DecisionQuestion) =>
    store.setDecisionQuestions(questions.map((current, i) => (i === index ? question : current)));

  return (
    <div className="flex w-full min-w-[468px] flex-col gap-3">
      <div>
        <Label className="block text-sm font-medium text-gray-700">Questions</Label>
        <p className="text-xs text-gray-500">
          The prompt is the input. Each question returns a typed answer with probabilities.
        </p>
      </div>
      <div className="flex max-h-[55vh] flex-col gap-3 overflow-y-auto">
        {questions.map((question, index) => (
          <DecisionQuestionCard
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            question={question}
            isDuplicateName={nameCounts[question.name] > 1}
            canRemove={questions.length > 1}
            onChange={updated => updateQuestion(index, updated)}
            onRemove={() => store.setDecisionQuestions(questions.filter((_, i) => i !== index))}
          />
        ))}
      </div>
      {questions.length < MAX_QUESTIONS && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => store.setDecisionQuestions([...questions, createQuestion(questions)])}
        >
          <PlusIcon className="mr-1 h-4 w-4" />
          Add question
        </Button>
      )}
    </div>
  );
});
